import { head, takeLast } from "ramda";
import { batch } from "react-redux";
import type { ThunkAction } from "redux-thunk";
import { TrackJS } from "trackjs";
import uuid from "uuid/v4";
import { findCardTypeByFirstDigit } from "helpers/credit_cards";
import {
  generateConfirmationCode,
  transformOrderInfo,
} from "helpers/transforms/digital_gift_cards";
import {
  getDigitalGiftCardsSettings,
  postCreditCardPayment,
} from "interfaces/digital_gift_cards";
import { registerCard } from "interfaces/order";

import type { ICreditCardForRegistry } from "types/credit_card";
import type {
  DigitalGiftCardOrderApiResponse,
  DigitalGiftCardsOrderApiRequest,
  DigitalGiftCardsSettings,
  DigitalGiftCardsStore,
  SubmitGiftCardCreditCardPaymentProps,
} from "types/digital_gift_cards";
import { type RemoteData, RequestStatus } from "types/requests";

export const SET_DIGITAL_GIFT_CARDS_SETTINGS =
  "digital_gift_cards/SET_DIGITAL_GIFT_CARDS_SETTINGS";

type SetDigitalGiftCardsSettingsAction = {
  type: typeof SET_DIGITAL_GIFT_CARDS_SETTINGS;
  settings: RemoteData<DigitalGiftCardsSettings>;
};

export const SET_PROCESSED_GIFT_CARD_PAYMENT =
  "digital_gift_cards/SET_PROCESSED_GIFT_CARD_PAYMENT";

type SetProcessedGiftCardPayment = {
  type: typeof SET_PROCESSED_GIFT_CARD_PAYMENT;
  processedGiftCardPayment: RemoteData<DigitalGiftCardOrderApiResponse>;
};

export const SET_CREDIT_CARD_REQUEST_STATUS =
  "digital_gift_cards/SET_CREDIT_CARD_REQUEST_STATUS";

type SetCreditCardRequestStatusAction = {
  type: typeof SET_CREDIT_CARD_REQUEST_STATUS;
  status: RequestStatus;
};

export type DigitalGiftCardsActionTypes =
  | SetDigitalGiftCardsSettingsAction
  | SetCreditCardRequestStatusAction
  | SetProcessedGiftCardPayment;

type DigitalGiftCardsThunkResult<R> = ThunkAction<
  R,
  DigitalGiftCardsStore,
  undefined,
  DigitalGiftCardsActionTypes
>;

function setDigitalGiftCardsSettingsRequestStatus(
  settings: RemoteData<DigitalGiftCardsSettings>
): SetDigitalGiftCardsSettingsAction {
  return {
    type: SET_DIGITAL_GIFT_CARDS_SETTINGS,
    settings,
  };
}

function setProcessedGiftCardPaymentRequestStatus(
  processedGiftCardPayment: RemoteData<DigitalGiftCardOrderApiResponse>
): SetProcessedGiftCardPayment {
  return {
    type: SET_PROCESSED_GIFT_CARD_PAYMENT,
    processedGiftCardPayment,
  };
}

export function setCreditCardRequestStatus(
  status: RequestStatus
): SetCreditCardRequestStatusAction {
  return {
    type: SET_CREDIT_CARD_REQUEST_STATUS,
    status,
  };
}

export function getAndUpdateDigitalGiftCardsSettings(): DigitalGiftCardsThunkResult<void> {
  return dispatch => {
    dispatch(
      setDigitalGiftCardsSettingsRequestStatus({
        type: RequestStatus.REQUESTING,
      })
    );
    getDigitalGiftCardsSettings()
      .then(settings => {
        dispatch(
          setDigitalGiftCardsSettingsRequestStatus({
            type: RequestStatus.SUCCESS,
            result: settings,
          })
        );
      })
      .catch(error => {
        dispatch(
          setDigitalGiftCardsSettingsRequestStatus({
            type: RequestStatus.FAILURE,
            reason: "error fetching settings from S3",
          })
        );
        // if we aren't getting settings back they either
        // don't exist or AWS is down
        TrackJS.track(`error fetching settings from S3: ${error}`);
      });
  };
}

function catchCardRegistrationError(error) {
  return dispatch => {
    dispatch(setCreditCardRequestStatus(RequestStatus.FAILURE));
    TrackJS.track(`error registering credit card: ${error}`);
  };
}

function catchPaymentError(error) {
  return dispatch => {
    batch(() => {
      // Attempt to get a more specific error message from the repsonse body
      error.response
        .json()
        .then(resp => {
          dispatch(
            setProcessedGiftCardPaymentRequestStatus({
              type: RequestStatus.FAILURE,
              reason:
                resp.error ??
                resp.message ??
                error.message ??
                "There was an error processing your request. Please check the form and try again.",
            })
          );
        })
        .catch(() => {
          dispatch(
            setProcessedGiftCardPaymentRequestStatus({
              type: RequestStatus.FAILURE,
              reason:
                "There was an error processing your request. Please check the form and try again.",
            })
          );
        })
        .finally(() =>
          dispatch(setCreditCardRequestStatus(RequestStatus.FAILURE))
        );
    });
    TrackJS.track(`error submitting order: ${error}`);
  };
}

export function submitCreditCardPayment({
  total,
  paymentMethodToken,
  cardInfo,
  formInfo,
  storePrettyUrl,
  sessionId,
}: SubmitGiftCardCreditCardPaymentProps): DigitalGiftCardsThunkResult<void> {
  const cardForRegistry: ICreditCardForRegistry = {
    firstName: cardInfo.firstName,
    lastName: cardInfo.lastName,
    paymentMethodToken,
    lastFour: takeLast(4, cardInfo.cardNumber),
    nameOnCard: `${cardInfo.firstName} ${cardInfo.lastName}`,
    line1: cardInfo.address1,
    line2: cardInfo.address2,
    city: cardInfo.city,
    state: cardInfo.state,
    zip: cardInfo.zip,
    cardType: findCardTypeByFirstDigit(
      head(cardInfo.cardNumber)
    ).prettyName.toLowerCase(),
    storePrettyUrl,
  };
  return dispatch => {
    registerCard(cardForRegistry)
      .then(({ consumerId, cardId }) => {
        const submitDate = new Date();
        const paymentRequestInfo: DigitalGiftCardsOrderApiRequest = {
          sessionId,
          storePrettyUrl,
          confirmationCode: generateConfirmationCode(storePrettyUrl),
          timePlaced: submitDate.toISOString(),
          submissionId: uuid(),
          cardUuid: cardId,
          consumerUuid: consumerId,
          consumerFirstName: cardInfo.firstName,
          consumerLastName: cardInfo.lastName,
          cvv: cardInfo.cvv,
          consumerPhoneNumber: formInfo.senderPhone.replace(/\D/g, ""),
          consumerEmail: formInfo.senderEmail,
          orderTotal: total.valueOf(),
          digitalGiftCards: [transformOrderInfo(formInfo)],
        };
        dispatch(
          setProcessedGiftCardPaymentRequestStatus({
            type: RequestStatus.REQUESTING,
          })
        );
        postCreditCardPayment(paymentRequestInfo)
          .then(processedPaymentInfo => {
            batch(() => {
              dispatch(
                setProcessedGiftCardPaymentRequestStatus({
                  type: RequestStatus.SUCCESS,
                  result: {
                    ...processedPaymentInfo,
                    lastFour: takeLast(4, cardInfo.cardNumber),
                  },
                })
              );
              dispatch(setCreditCardRequestStatus(RequestStatus.SUCCESS));
            });
          })
          .catch(error => {
            dispatch(catchPaymentError(error));
          });
      })
      .catch(error => {
        dispatch(catchCardRegistrationError(error));
      });
  };
}
